'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { cn, isRouteActive } from '../lib/utils';
import { Trans } from './trans';

/**
 * Horizontal route-tab bar (each item is a distinct page/URL, active state
 * derived from the pathname). Linear/GitHub style: plain text tabs with a
 * single coral underline flush with the container's bottom border on the
 * active tab — no pill, no detached second line. Inactive tabs preview a
 * subtle gray underline on hover.
 *
 * The bottom border belongs to the CONSUMER's wrapper (e.g. a `<nav border-b>`
 * or a sticky `<header border-b>`); each active item's `-mb-px` underline pulls
 * onto that border so the two read as one line. Pass horizontal inset (e.g.
 * `px-4 lg:px-8`) via `className` to align the tabs with surrounding chrome.
 *
 * Canonical for BOTH the record workspace shell and the marketplace header —
 * do not fork a second underline-tab style.
 */
export function BorderedNavigationMenu(
  props: React.PropsWithChildren<{ className?: string }>,
) {
  return (
    <div className={cn('flex items-center gap-x-6', props.className)}>
      {props.children}
    </div>
  );
}

export function BorderedNavigationMenuItem(props: {
  path: string;
  label: React.ReactNode | string;
  end?: boolean | ((path: string) => boolean);
  active?: boolean;
  badge?: React.ReactNode;
  className?: string;
  /** @deprecated Merged into `className`; kept for call-site compatibility. */
  buttonClassName?: string;
}) {
  const pathname = usePathname();

  const active = props.active ?? isRouteActive(props.path, pathname, props.end);

  return (
    <Link
      href={props.path}
      aria-current={active ? 'page' : undefined}
      className={cn(
        '-mb-px flex items-center gap-2 border-b-2 py-3 text-sm font-medium whitespace-nowrap transition-colors',
        active
          ? 'border-primary text-foreground'
          : 'text-muted-foreground hover:text-foreground hover:border-border border-transparent',
        props.className,
        props.buttonClassName,
      )}
    >
      {typeof props.label === 'string' ? (
        <Trans i18nKey={props.label} defaults={props.label} />
      ) : (
        props.label
      )}

      {props.badge ? <span>{props.badge}</span> : null}
    </Link>
  );
}
